import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useContextMenu, useTooltip, useDebounce } from '../../hooks';
import { Icon } from '../';

import './ContextMenu.css';

export const ContextMenu = ({ options }) => {
  const { contextMenu, hideContextMenu } = useContextMenu();

  const { showTooltip, hideTooltip } = useTooltip();

  const portalRef = document.getElementById('portal');

  // Global listeners for closing the menu
  useEffect(() => {
    if (!contextMenu.show) return;

    function handleGlobalMouseDown(e) {
      // We close on any click; if you want "click inside doesn't close",
      // you can stopPropagation in the menu component.
      hideContextMenu();
    }

    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        hideContextMenu();
      }
    }

    function handleScrollOrResize() {
      // Native menus usually close if the page layout changes
      hideContextMenu();
    }

    window.addEventListener('mousedown', handleGlobalMouseDown);
    //window.addEventListener("contextmenu", handleGlobalMouseDown);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      window.removeEventListener('mousedown', handleGlobalMouseDown);
      //window.removeEventListener("contextmenu", handleGlobalMouseDown);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [contextMenu.show, hideContextMenu]);

  /*
  const { show, contextMenu, hideContextMenu } = useContextMenu()
  const {showTooltip, hideTooltip} = useTooltip()

  let { options } = contextMenu
  const offset = 0

  const [showContextMenu, setShowContextMenu] = useState(false)
  //const [mousePos, setMousePos] = useState(undefined)
  //const [prevMousePos, _setPrevMousePos] = useState(undefined)
  const tmpRef = useRef()
  
  const contextMenuRef = tmpRef
  const portalRef = document.getElementById("portal")

  let e = {}

  const mousePosRef = React.useRef();
  const setMousePos = data => {
    mousePosRef.current = data;
    //_setMousePos(prev => data);
  };

  //const prevMousePosRef = React.useRef(prevMousePos);
  //const setPrevMousePos = data => {
  //  prevMousePosRef.current = data;
  //  _setPrevMousePos(prev => data);
  //};

  useEffect( () => {
    window.addEventListener('mousemove', trackMouse)
    window.addEventListener('click', trackClick) 
    //window.addEventListener('contextmenu', trackClick) 
    return () => {
      window.removeEventListener('mousemove', trackMouse)
      window.removeEventListener('click', trackClick)
      //window.removeEventListener('contextmenu', trackClick)
    }
  }, [])
 
  useEffect( () => {
    setShowContextMenu(show)
  }, [show])

  useEffect( () => {
    showContextMenu && positionContextMenu()
  }, [showContextMenu])

  const trackClick = (event) => {
    //event.preventDefault()
    hideContextMenu()
    hideTooltip()
    setShowContextMenu(false)
  }

  const trackMouse = (event) => { // Debounce to reduce number of calls to setState
    e = {
      pageX: event.pageX,
      pageY: event.pageY
    }

    setMousePos(e)

  }

  const positionContextMenu = () => {
    if (contextMenuRef && contextMenuRef.current && mousePosRef.current) {
      if (contextMenuRef.current.offsetWidth + mousePosRef.current.pageX + offset + 10 < window.innerWidth) {      
        contextMenuRef.current.style.removeProperty('right')
        contextMenuRef.current.style.left = (mousePosRef.current.pageX + offset) + "px"
      } else {    
        contextMenuRef.current.style.removeProperty('left') 
        contextMenuRef.current.style.right = "10px"
      }

      if (contextMenuRef.current.offsetHeight + mousePosRef.current.pageY + offset + 10 < window.innerHeight) {
        contextMenuRef.current.style.top = (mousePosRef.current.pageY + offset) + "px"
      } else {
        contextMenuRef.current.style.top = (mousePosRef.current.pageY - contextMenuRef.current.offsetHeight - offset) + "px"
      }
    } else { // fix to prevent tooltip to render in strange places on some random occasions when some of the refs are not set
      setShowContextMenu(false)
    }
  }
*/

  const handleClick = onClick => {
    hideContextMenu();
    onClick && onClick();
  };

  const renderOption = (option, index) => {
    let element;
    switch (option.type) {
      case 'divider':
        element = <div></div>;
        break;
      default:
        element = (
          <React.Fragment>
            <span className="context-menu-item__icon">
              <Icon name={option.icon} />
            </span>
            {option.label}
          </React.Fragment>
        );
        break;
    }

    return (
      <div
        key={index}
        className={
          'context-menu-item context-menu-item__' +
          (option.type || 'link') +
          (option.disabled ? ' disabled' : '')
        }
        onClick={() => !option.disabled && handleClick(option?.onClick)}
        onMouseEnter={() => {
          option.tooltip &&
            showTooltip({
              title: !option.disabled ? option.tooltip : 'This node is already in focus',
            });
          option.onMouseEnter && option.onMouseEnter();
        }}
        onMouseLeave={() => {
          option.tooltip && hideTooltip();
          option.onMouseLEave && option.onMouseLeave();
        }}
        role="menuitem"
      >
        {element}
      </div>
    );
  };

  return (
    contextMenu?.show &&
    createPortal(
      <ContextMenuOverlay x={contextMenu.x} y={contextMenu.y} onRequestClose={hideContextMenu}>
        <div className="context-menu-body">
          {contextMenu?.options &&
            contextMenu.options.map((option, index) => {
              return renderOption(option, index);
            })}
        </div>
      </ContextMenuOverlay>,
      portalRef
    )
  );
};

function ContextMenuOverlay({ x, y, children, onRequestClose }) {
  const menuRef = useRef(null);
  const [position, setPosition] = useState({ x, y });

  // Clamp to viewport so it doesn’t overflow
  useLayoutEffect(() => {
    const menuEl = menuRef.current;
    if (!menuEl) return;

    const { offsetWidth: w, offsetHeight: h } = menuEl;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const clampedX = Math.min(x, vw - w - 4);
    const clampedY = Math.min(y, vh - h - 4);

    setPosition({
      x: Math.max(4, clampedX),
      y: Math.max(4, clampedY),
    });
  }, [x, y, children]);

  const handleMenuMouseDown = e => {
    // Prevent click inside menu from bubbling to window and closing it
    e.stopPropagation();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 98,
        pointerEvents: 'none', // let events pass through except the menu
      }}
    >
      <div
        ref={menuRef}
        className="ContextMenu"
        onMouseDown={handleMenuMouseDown}
        onContextMenu={e => e.preventDefault()}
        style={{
          top: position.y,
          left: position.x,
        }}
        role="menu"
      >
        {children}
      </div>
    </div>
  );
}
